import React, { useEffect, useState } from 'react';
import { Grid, Button, FormControl, FormControlLabel, FormGroup, FormLabel, Checkbox, Select, Input, MenuItem, Typography, Box } from '@material-ui/core';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import HomeIcon from '@material-ui/icons/Home';
import FilterIcon from '@material-ui/icons/FilterList';
import { CommunityIcon, DivisionIcon } from 'global/components/PerformIcons';
import { useSelector, useDispatch } from 'react-redux';
import { get, set, size, isArray, xor } from 'global/lib/lodash';
import setFilter from 'redux/actions/setFilter';
import { Filter } from 'global/utils/filter';
import useStyles from 'global/styles/filters';
import { useScreen } from 'global/styles';
import DiagContainer from 'global/components/diag-container';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { sortAlphaNum } from 'global/utils';
import { FilterChips } from 'global/components/filterchips';
import { Ownership, QualityType, RealmTypes } from 'global/lib/filter';
import clsx from 'clsx';

const ICONS = {
  HomeIcon,
  CommunityIcon,
  DivisionIcon,
};

export default function PerformReportsFilters(props) {
  const { display } = props;
  const vendors = get(props, 'vendors');
  const [showFilters, setShowFilters] = useState(false);
  const filter = useSelector((state) => get(state, `filter.reports.${display}`, Filter()));
  const user = useSelector((state) => get(state, 'auth.user'));
  const dispatch = useDispatch();
  const { realm_type, start_date, end_date, scorecard_id, ownership, superset_id, user_id, quality_type } = filter;

  const scorecards = useRetrieveData(
    { url: '/scorecards' },
    {
      build: ['scorecards'],
      useCache: { reload: 20, store: 'data.scorecards' },
    }
  );
  const content_realm_id = get(user, 'realm.id');
  const users = useRetrieveData(
    { url: '/users' },
    {
      waitFor: display === 'photo' ? undefined : null,
      build: ['users'],
      params: {
        filter: content_realm_id && {
          content_realm_id,
        },
      },
      useCache: { reload: 1 },
    }
  );
  const supersets = useRetrieveData(
    { url: '/supersets' },
    {
      build: ['supersets'],
      useCache: { reload: 20 },
    }
  );

  const prefixToUse = 'vendor';

  const { isMobile } = useScreen();

  const classes = useStyles();

  useEffect(() => {
    setShowFilters(!isMobile);
  }, [isMobile]);

  const changeFilter = (filt) => {
    dispatch(setFilter('reports', display, filt));
  };

  function handleStartDateChange(date) {
    changeFilter(set({ ...filter }, `start_date`, date));
  }

  function handleEndDateChange(date) {
    changeFilter(set({ ...filter }, `end_date`, date));
  }

  function handleScorecardChange(e) {
    changeFilter(set({ ...filter }, `scorecard_id`, e.target.value));
  }

  function handleSystemChange(e) {
    changeFilter(set({ ...filter }, `superset_id`, e.target.value));
  }

  function handleAuthorChange(e) {
    changeFilter(set({ ...filter }, `user_id`, e.target.value));
  }

  function handleQualityChange(e) {
    changeFilter(set({ ...filter }, `quality_type`, e.target.value));
  }

  const handleRealmType = (e, val) => {
    changeFilter(set({ ...filter }, `realm_type`, xor(realm_type, [val])));
  };

  const handleOwnership = (e, val) => {
    if (val !== null) {
      changeFilter(set({ ...filter }, `ownership`, val));
    }
  };

  const handleVendorChange = (e) => {
    const vend = get(vendors, 'data', []).find((v) => v.id == e.target.value);
    changeFilter(set({ ...filter }, `${prefixToUse}_id`, get(vend, `${prefixToUse}Id`, null)));
  };

  function toggleFilters() {
    setShowFilters(!showFilters);
  }

  const hasVendors = size(get(vendors, 'data')) > 0;
  const client = get(vendors, 'data', []).find((v) => get(v, [prefixToUse, 'id']) == get(filter, `${prefixToUse}_id`));

  const noMyFilter = ['data', 'photo'].indexOf(display) > -1;

  return (
    <Box displayPrint={'none'}>
      {isMobile && (
        <div className={clsx(classes.filterRow, classes.filterRowMobile)}>
          <Button className="FilterBtn" aria-label="filters" size="small" onClick={toggleFilters}>
            <FilterIcon fontSize="inherit" className={classes.icon} /> {'Filters'}
          </Button>
          <FilterChips id={`reports.${display}`} />
        </div>
      )}

      <DiagContainer useDialog={isMobile} onClose={toggleFilters} open={showFilters}>
        <Typography variant="overline">Filters</Typography>

        <form autoComplete="off" margin="normal" className={showFilters ? '' : classes.hidden}>
          <Grid container>
            {!noMyFilter && (
              <Grid item xs={12} className={classes.formGrid}>
                <ToggleButtonGroup exclusive className={classes.btnGroup} size="small" value={ownership} onChange={handleOwnership}>
                  {Ownership.map((o) => (
                    <ToggleButton className={classes.smallText} value={o.value}>
                      {o.title}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
            )}

            <Grid item xs={12} className={classes.formGrid}>
              <FormControl variant="standard" className={classes.formItem}>
                <FormLabel htmlFor="Standard-scorecard-simple">Scorecard</FormLabel>
                <Select value={scorecard_id} onChange={handleScorecardChange} input={<Input name="scorecard" id="Standard-scorecard-simple" />}>
                  <MenuItem value={0}>
                    <em>Any</em>
                  </MenuItem>
                  {size(scorecards.data) &&
                    scorecards.data.map((card, i) => {
                      return (
                        <MenuItem key={`${card.id}-${i}`} value={card.id}>
                          {card.name}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} className={classes.formGrid}>
              <FormControl variant="standard" className={classes.formItem}>
                <FormLabel>{'Location Type'}</FormLabel>
                <FormGroup>
                  {RealmTypes.map((rt, i) => {
                    const ICO = ICONS[rt.icon];
                    return (
                      <FormControlLabel
                        key={i}
                        control={<Checkbox checked={isArray(realm_type) && realm_type.includes(rt.value)} onChange={(e) => handleRealmType(e, rt.value)} value={rt.value} />}
                        label={
                          <div className={classes.checkRow}>
                            <ICO className={classes.checkIcon} />
                            {rt.title}
                          </div>
                        }
                      />
                    );
                  })}
                </FormGroup>
              </FormControl>
            </Grid>

            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <Grid item xs={12} className={classes.formGrid}>
                <KeyboardDatePicker
                  id="mui-pickers-date_start"
                  label="Start Date"
                  format="MM/dd/yyyy"
                  autoOk={true}
                  value={start_date}
                  onChange={handleStartDateChange}
                  className={classes.formItem}
                  KeyboardButtonProps={{
                    'aria-label': 'start date',
                  }}
                />
              </Grid>
              <Grid item xs={12} className={classes.formGrid}>
                <KeyboardDatePicker
                  id="mui-pickers-date_end"
                  label="End Date"
                  format="MM/dd/yyyy"
                  autoOk={true}
                  value={end_date}
                  onChange={handleEndDateChange}
                  className={classes.formItem}
                  KeyboardButtonProps={{
                    'aria-label': 'end date',
                  }}
                />
              </Grid>
            </MuiPickersUtilsProvider>

            {hasVendors && (
              <Grid item xs={12} className={classes.formGrid}>
                <FormControl variant="standard" className={classes.formItem}>
                  <FormLabel htmlFor="Standard-scorecard-simple">Company</FormLabel>
                  <Select value={get(client, 'id', 0)} onChange={handleVendorChange}>
                    <MenuItem value={0}>
                      <em>Any</em>
                    </MenuItem>
                    {get(vendors, 'data', [])
                      .sort((a, b) => sortAlphaNum(a, b, 'vendor.name'))
                      .map((vend) => {
                        return (
                          <MenuItem key={`${vend.id}`} value={vend.id}>
                            {get(vend, [prefixToUse, 'name'], '')}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {display === 'photo' && (
              <Grid item xs={12} className={classes.formGrid}>
                <FormControl variant="standard" className={classes.formItem}>
                  <FormLabel htmlFor="Standard-scorecard-simple">System</FormLabel>
                  <Select value={superset_id || 0} onChange={handleSystemChange}>
                    <MenuItem value={0}>
                      <em>Any</em>
                    </MenuItem>
                    {!!size(supersets.data) &&
                      (supersets.data || []).map((card, i) => {
                        return (
                          <MenuItem key={`${card.id}-${i}`} value={card.id}>
                            {card.name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>

                <FormControl variant="standard" className={classes.formItem}>
                  <FormLabel htmlFor="Standard-scorecard-simple">Author</FormLabel>
                  <Select value={user_id || 0} onChange={handleAuthorChange}>
                    <MenuItem value={0}>
                      <em>Any</em>
                    </MenuItem>
                    {!!size(users.data) &&
                      users.data.map((user, i) => {
                        return (
                          <MenuItem key={`${user.id}-${i}`} value={user.id}>
                            {user.name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>

                <FormControl variant="standard" className={classes.formItem}>
                  <FormLabel htmlFor="Standard-scorecard-simple">Quality</FormLabel>
                  <Select value={quality_type} onChange={handleQualityChange}>
                    {QualityType.map((qt) => (
                      <MenuItem value={qt.value}>{qt.value === 1 ? <em>{qt.title}</em> : qt.title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </form>
      </DiagContainer>
    </Box>
  );
}
